/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * UI domain - Minimap visualization for spatial awareness and navigation
 * minimap.ui.tsx - React/SVG-based minimap component with world-wrapping support
 */

import { ASTEROID_SIZES, COLORS, WORLD } from "@game/config";
import { useGameState } from "@game/state";
import type { Flag, MOBABase, MOBANPC } from "@game/state";
import type {
  Asteroid,
  Bullet,
  Pellet,
  Player,
} from "../../../../packages/protocol/src/generated/messages";
import { memo, useState, useEffect, useRef } from "react";
import type { ReactElement } from "react";

// ============================================================================
// CONSTANTS
// ============================================================================

const MINIMAP_SIZE = 320;
const MINIMAP_MARGIN = 20;
const MOBILE_BREAKPOINT = 768;

// ============================================================================
// MOBILE DETECTION
// ============================================================================

/**
 * Detect if device is mobile based on user agent and viewport width
 * Used to determine minimap display mode (toggle vs always-visible)
 */
function detectMobile(): boolean {
  const hasMobileUserAgent = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent,
  );
  const hasNarrowViewport = window.innerWidth < MOBILE_BREAKPOINT;
  return hasMobileUserAgent || hasNarrowViewport;
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface MinimapPosition {
  x: number;
  y: number;
}

interface WrappedEntity {
  position: MinimapPosition;
  visible: boolean;
}

// ============================================================================
// COORDINATE UTILITIES
// ============================================================================

/**
 * Calculate world-wrapped relative position from world coordinates
 * Handles toroidal world wrapping for seamless edge transitions
 *
 * @param worldX - World X coordinate of entity
 * @param worldY - World Y coordinate of entity
 * @param localPlayerX - Local player X coordinate (camera center)
 * @param localPlayerY - Local player Y coordinate (camera center)
 * @param worldWidth - World width dimension
 * @param worldHeight - World height dimension
 * @returns Wrapped relative delta coordinates
 */
function calculateWrappedDelta(
  worldX: number,
  worldY: number,
  localPlayerX: number,
  localPlayerY: number,
  worldWidth: number,
  worldHeight: number,
): { dx: number; dy: number } {
  let dx = worldX - localPlayerX;
  let dy = worldY - localPlayerY;

  // Handle world wrapping (toroidal topology)
  if (Math.abs(dx) > worldWidth / 2) {
    dx = dx > 0 ? dx - worldWidth : dx + worldWidth;
  }
  if (Math.abs(dy) > worldHeight / 2) {
    dy = dy > 0 ? dy - worldHeight : dy + worldHeight;
  }

  return { dx, dy };
}

/**
 * Transform world coordinates to minimap screen coordinates
 * Centers on local player and scales to minimap dimensions
 *
 * @param worldX - World X coordinate
 * @param worldY - World Y coordinate
 * @param localPlayerPos - Local player position (camera center)
 * @param scale - Minimap scaling factor
 * @returns Minimap position and visibility flag
 */
function worldToMinimapPosition(
  worldX: number,
  worldY: number,
  localPlayerPos: { x: number; y: number },
  scale: number,
): WrappedEntity {
  const { dx, dy } = calculateWrappedDelta(
    worldX,
    worldY,
    localPlayerPos.x,
    localPlayerPos.y,
    WORLD.w,
    WORLD.h,
  );

  const minimapX = MINIMAP_SIZE / 2 + dx * scale;
  const minimapY = MINIMAP_SIZE / 2 + dy * scale;

  // Check if entity is within minimap bounds (with 2px padding for edge cases)
  const visible =
    minimapX >= -2 &&
    minimapX <= MINIMAP_SIZE + 2 &&
    minimapY >= -2 &&
    minimapY <= MINIMAP_SIZE + 2;

  return {
    position: { x: minimapX, y: minimapY },
    visible,
  };
}

// ============================================================================
// ENTITY RENDERING COMPONENTS
// ============================================================================

/**
 * Generate deterministic hash from string (matches canvas implementation)
 */
function hashString(str: string): number {
  return str.split("").reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
}

/**
 * Generate asteroid polygon vertices with procedural variation
 * Matches canvas rendering: 8-sided irregular polygon
 */
function generateAsteroidVertices(
  centerX: number,
  centerY: number,
  radius: number,
  rotation: number,
  asteroidId: string,
): string {
  const sides = 8;
  const hash = hashString(asteroidId);
  const points: string[] = [];

  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * Math.PI * 2 + rotation;
    const variation = 0.7 + ((Math.abs(hash + i * 123) % 1000) / 1000) * 0.6;
    const r = radius * variation;
    const x = centerX + Math.cos(angle) * r;
    const y = centerY + Math.sin(angle) * r;
    points.push(`${x},${y}`);
  }

  return points.join(" ");
}

/**
 * Render asteroid markers on minimap
 */
const MinimapAsteroids = memo(
  ({
    asteroids,
    localPlayerPos,
    scale,
  }: {
    asteroids: Map<string, Asteroid>;
    localPlayerPos: { x: number; y: number };
    scale: number;
  }) => {
    const elements: ReactElement[] = [];

    for (const [id, asteroid] of asteroids) {
      if (!asteroid.pos) continue;

      const entity = worldToMinimapPosition(
        asteroid.pos.x,
        asteroid.pos.y,
        localPlayerPos,
        scale,
      );

      if (entity.visible) {
        const worldRadius = ASTEROID_SIZES[asteroid.sizeLevel] || 20;
        const minimapRadius = worldRadius * scale;

        const points = generateAsteroidVertices(
          entity.position.x,
          entity.position.y,
          minimapRadius,
          asteroid.rotation,
          asteroid.id,
        );

        elements.push(
          <polygon
            key={id}
            points={points}
            fill="none"
            stroke="#ffffff"
            strokeWidth={0.5}
            strokeOpacity={0.6}
          />,
        );
      }
    }

    return <g>{elements}</g>;
  },
);

MinimapAsteroids.displayName = "MinimapAsteroids";

/**
 * Get pellet color based on value tier
 */
function getPelletColor(value: number): string {
  if (value >= 1000) return "#ffff00"; // Yellow - legendary
  if (value >= 100) return "#8a2be2"; // Purple - epic
  if (value >= 10) return "#3b82f6"; // Blue - rare
  return "#22c55e"; // Green - common
}

/**
 * Get pellet size based on value tier
 */
function getPelletSize(value: number): number {
  if (value >= 1000) return 2.5;
  if (value >= 100) return 2;
  if (value >= 10) return 1.5;
  return 1;
}

/**
 * Render pellet markers on minimap
 */
const MinimapPellets = memo(
  ({
    pellets,
    localPlayerPos,
    scale,
  }: {
    pellets: Map<string, Pellet>;
    localPlayerPos: { x: number; y: number };
    scale: number;
  }) => {
    const elements: ReactElement[] = [];

    for (const [id, pellet] of pellets) {
      if (!pellet.pos) continue;

      const entity = worldToMinimapPosition(
        pellet.pos.x,
        pellet.pos.y,
        localPlayerPos,
        scale,
      );

      if (entity.visible) {
        elements.push(
          <circle
            key={id}
            cx={entity.position.x}
            cy={entity.position.y}
            r={getPelletSize(pellet.value)}
            fill={getPelletColor(pellet.value)}
          />,
        );
      }
    }

    return <g>{elements}</g>;
  },
);

MinimapPellets.displayName = "MinimapPellets";

/**
 * Render CTF flags on minimap
 */
const MinimapFlags = memo(
  ({
    flags,
    localPlayerPos,
    scale,
  }: {
    flags: Map<string, Flag>;
    localPlayerPos: { x: number; y: number };
    scale: number;
  }) => {
    const elements: ReactElement[] = [];

    for (const [id, flag] of flags) {
      if (!flag.pos) continue;

      const entity = worldToMinimapPosition(
        flag.pos.x,
        flag.pos.y,
        localPlayerPos,
        scale,
      );

      // ALWAYS render flags regardless of visibility (they're important!)
      const flagColor = flag.teamId === 0 ? COLORS.BLUE : COLORS.RED;
      const flagSize = 8;

      elements.push(
        <g key={id}>
          {/* Flag pole */}
          <line
            x1={entity.position.x}
            y1={entity.position.y}
            x2={entity.position.x}
            y2={entity.position.y - flagSize * 2}
            stroke={flagColor}
            strokeWidth={1.5}
          />
          {/* Flag fabric */}
          <polygon
            points={`${entity.position.x},${entity.position.y - flagSize * 2} ${entity.position.x + flagSize},${entity.position.y - flagSize * 1.5} ${entity.position.x},${entity.position.y - flagSize}`}
            fill={flagColor}
            stroke={flagColor}
            strokeWidth={1}
          />
          {/* State indicator for carried/dropped */}
          {flag.state === "carried" && (
            <circle
              cx={entity.position.x}
              cy={entity.position.y}
              r={flagSize * 2}
              fill="none"
              stroke={flagColor}
              strokeWidth={1.5}
              opacity={0.6}
            />
          )}
          {flag.state === "dropped" && (
            <circle
              cx={entity.position.x}
              cy={entity.position.y}
              r={flagSize * 1.5}
              fill={flagColor}
              opacity={0.4}
            />
          )}
        </g>,
      );
    }

    return <g>{elements}</g>;
  },
);

MinimapFlags.displayName = "MinimapFlags";

/**
 * Render CTF team bases on minimap
 */
const MinimapBases = memo(
  ({
    gameMode,
    localPlayerPos,
    scale,
  }: {
    gameMode: string | null;
    localPlayerPos: { x: number; y: number };
    scale: number;
  }) => {
    // Only render bases in CTF mode
    if (gameMode !== "ctf") {
      return null;
    }

    const CAPTURE_RADIUS = 50; // Smaller capture zone
    const SPAWN_RADIUS = 500; // Larger spawn zone (10x)
    const basePositions = [
      { x: 0, y: 0, teamId: 0 }, // Team 0 (Blue)
      { x: WORLD.w * 0.5, y: WORLD.h * 0.5, teamId: 1 }, // Team 1 (Red)
    ];

    const elements: ReactElement[] = [];

    for (const base of basePositions) {
      const entity = worldToMinimapPosition(
        base.x,
        base.y,
        localPlayerPos,
        scale,
      );

      if (entity.visible) {
        const baseColor = base.teamId === 0 ? COLORS.BLUE : COLORS.RED;
        const spawnRadiusScaled = SPAWN_RADIUS * scale;
        const captureRadiusScaled = CAPTURE_RADIUS * scale;

        elements.push(
          <g key={`base-${base.teamId}`}>
            {/* Outer spawn zone ring (large, subtle) */}
            <circle
              cx={entity.position.x}
              cy={entity.position.y}
              r={spawnRadiusScaled}
              fill="none"
              stroke={baseColor}
              strokeWidth={0.5}
              opacity={0.3}
            />
            {/* Inner capture zone ring (small, bright) */}
            <circle
              cx={entity.position.x}
              cy={entity.position.y}
              r={captureRadiusScaled}
              fill="none"
              stroke={baseColor}
              strokeWidth={0.8}
              opacity={0.6}
            />
            {/* Center marker */}
            <circle
              cx={entity.position.x}
              cy={entity.position.y}
              r={2}
              fill={baseColor}
              opacity={0.8}
            />
          </g>,
        );
      }
    }

    return <g>{elements}</g>;
  },
);

MinimapBases.displayName = "MinimapBases";

const MOBA_TEAM_COLORS = {
  0: "#3b82f6", // Blue team
  1: "#ef4444", // Red team
} as const;

/**
 * Render MOBA bases on minimap as hexagons
 */
const MinimapMOBABases = memo(
  ({
    mobaBases,
    localPlayerPos,
    scale,
  }: {
    mobaBases: Map<string, MOBABase>;
    localPlayerPos: { x: number; y: number };
    scale: number;
  }) => {
    const elements: ReactElement[] = [];

    for (const [id, base] of mobaBases) {
      if (!base.pos) continue;

      const entity = worldToMinimapPosition(
        base.pos.x,
        base.pos.y,
        localPlayerPos,
        scale,
      );

      const teamColor =
        MOBA_TEAM_COLORS[base.teamId as keyof typeof MOBA_TEAM_COLORS] ||
        "#888888";
      const hexRadius = 6;

      const hexPoints: string[] = [];
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 2;
        const px = entity.position.x + hexRadius * Math.cos(angle);
        const py = entity.position.y + hexRadius * Math.sin(angle);
        hexPoints.push(`${px},${py}`);
      }

      elements.push(
        <g key={id}>
          <polygon
            points={hexPoints.join(" ")}
            fill={base.health > 0 ? teamColor : "#333333"}
            stroke={base.health > 0 ? "#ffffff" : "#666666"}
            strokeWidth={1}
            opacity={0.9}
          />
          {base.health <= 0 && (
            <line
              x1={entity.position.x - 4}
              y1={entity.position.y - 4}
              x2={entity.position.x + 4}
              y2={entity.position.y + 4}
              stroke="#ff0000"
              strokeWidth={2}
            />
          )}
        </g>,
      );
    }

    return <g>{elements}</g>;
  },
);

MinimapMOBABases.displayName = "MinimapMOBABases";

/**
 * Render MOBA NPCs on minimap as tiny dots
 */
const MinimapMOBANPCs = memo(
  ({
    mobaNpcs,
    localPlayerPos,
    scale,
  }: {
    mobaNpcs: Map<string, MOBANPC>;
    localPlayerPos: { x: number; y: number };
    scale: number;
  }) => {
    const elements: ReactElement[] = [];

    for (const [id, npc] of mobaNpcs) {
      if (!npc.pos) continue;

      const entity = worldToMinimapPosition(
        npc.pos.x,
        npc.pos.y,
        localPlayerPos,
        scale,
      );

      if (entity.visible) {
        const teamColor =
          MOBA_TEAM_COLORS[npc.teamId as keyof typeof MOBA_TEAM_COLORS] ||
          "#888888";

        elements.push(
          <circle
            key={id}
            cx={entity.position.x}
            cy={entity.position.y}
            r={2}
            fill={teamColor}
          />,
        );
      }
    }

    return <g>{elements}</g>;
  },
);

MinimapMOBANPCs.displayName = "MinimapMOBANPCs";

/**
 * Render bullet markers on minimap
 */
const MinimapBullets = memo(
  ({
    bullets,
    players,
    localPlayerPos,
    localPlayerId,
    scale,
  }: {
    bullets: Map<string, Bullet>;
    players: Map<string, Player>;
    localPlayerPos: { x: number; y: number };
    localPlayerId: string;
    scale: number;
  }) => {
    const elements: ReactElement[] = [];

    for (const [id, bullet] of bullets) {
      if (!bullet.pos) continue;

      const entity = worldToMinimapPosition(
        bullet.pos.x,
        bullet.pos.y,
        localPlayerPos,
        scale,
      );

      if (entity.visible) {
        // Get bullet owner's color (handle undefined ownerId)
        const bulletOwner =
          bullet.ownerId !== undefined
            ? players.get(bullet.ownerId)
            : undefined;
        const isLocalBullet = bullet.ownerId === localPlayerId;
        const bulletColor =
          bulletOwner?.color ?? (isLocalBullet ? "#00ff00" : "#ff0000");

        elements.push(
          <circle
            key={id}
            cx={entity.position.x}
            cy={entity.position.y}
            r={0.8}
            fill={bulletColor}
          />,
        );
      }
    }

    return <g>{elements}</g>;
  },
);

MinimapBullets.displayName = "MinimapBullets";

/**
 * Render triangular player indicator showing rotation
 */
const PlayerTriangle = memo(
  ({
    x,
    y,
    rotation,
    playerScale,
    isLocalPlayer,
    color,
  }: {
    x: number;
    y: number;
    rotation: number;
    playerScale: number;
    isLocalPlayer: boolean;
    color?: string;
  }) => {
    const baseSize = 3;
    const minimapPlayerScaleMultiplier = 0.5;
    const maxScale = isLocalPlayer ? 3.5 : 3.0;

    const size = Math.max(
      isLocalPlayer ? 6 : 3,
      baseSize * Math.min(playerScale * minimapPlayerScaleMultiplier, maxScale),
    );

    const height = size * 0.8;
    const width = size * 1.2;

    // Triangle vertices (pointing right at 0 rotation)
    const tip = { x: width * 0.6, y: 0 };
    const bottomLeft = { x: -width * 0.4, y: height * 0.5 };
    const topLeft = { x: -width * 0.4, y: -height * 0.5 };

    // Rotate vertices around origin
    const rotatePoint = (px: number, py: number) => {
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      return {
        x: px * cos - py * sin + x,
        y: px * sin + py * cos + y,
      };
    };

    const rotatedTip = rotatePoint(tip.x, tip.y);
    const rotatedBottomLeft = rotatePoint(bottomLeft.x, bottomLeft.y);
    const rotatedTopLeft = rotatePoint(topLeft.x, topLeft.y);

    const pathData = `M ${rotatedTip.x},${rotatedTip.y} L ${rotatedBottomLeft.x},${rotatedBottomLeft.y} L ${rotatedTopLeft.x},${rotatedTopLeft.y} Z`;

    const strokeColor = color || (isLocalPlayer ? "#00ff00" : "#ff0000");

    return (
      <path d={pathData} fill="none" stroke={strokeColor} strokeWidth={1} />
    );
  },
);

PlayerTriangle.displayName = "PlayerTriangle";

/**
 * Render all player indicators on minimap
 */
const MinimapPlayers = memo(
  ({
    players,
    localPlayerId,
    localPlayerPos,
    scale,
    flags = [],
  }: {
    players: Map<string, Player>;
    localPlayerId: string;
    localPlayerPos: { x: number; y: number };
    scale: number;
    flags?: Array<{
      id: string;
      teamId: number;
      state: string;
      carrierId?: string;
    }>;
  }) => {
    const elements: ReactElement[] = [];

    // Helper to check if player is carrying a flag
    const getCarriedFlagTeamId = (playerId: string): number | null => {
      if (!Array.isArray(flags)) return null;
      const carriedFlag = flags.find(
        (f) => f.state === "carried" && f.carrierId === playerId,
      );
      return carriedFlag ? carriedFlag.teamId : null;
    };

    // Render remote players first (so local player appears on top)
    for (const [id, player] of players) {
      if (id === localPlayerId || !player.pos) continue;

      const entity = worldToMinimapPosition(
        player.pos.x,
        player.pos.y,
        localPlayerPos,
        scale,
      );

      if (entity.visible) {
        const carriedFlagTeam = getCarriedFlagTeamId(id);

        elements.push(
          <PlayerTriangle
            key={id}
            x={entity.position.x}
            y={entity.position.y}
            rotation={player.rotation || 0}
            playerScale={player.scale || 1.0}
            isLocalPlayer={false}
            color={player.color}
          />,
        );

        // Add pulsing ring if carrying flag
        if (carriedFlagTeam !== null) {
          const ringColor = carriedFlagTeam === 0 ? "#61afef" : "#e06c75";
          elements.push(
            <circle
              key={`${id}-flag-ring`}
              cx={entity.position.x}
              cy={entity.position.y}
              r={10}
              fill="none"
              stroke={ringColor}
              strokeWidth={2}
              opacity={0.8}
              className="pulse-ring"
            />,
          );
        }
      }
    }

    // Render local player at center (always visible)
    const localPlayer = players.get(localPlayerId);
    if (localPlayer) {
      const carriedFlagTeam = getCarriedFlagTeamId(localPlayerId);

      elements.push(
        <PlayerTriangle
          key={localPlayerId}
          x={MINIMAP_SIZE / 2}
          y={MINIMAP_SIZE / 2}
          rotation={localPlayer.rotation || 0}
          playerScale={localPlayer.scale || 1.0}
          isLocalPlayer={true}
          color={localPlayer.color}
        />,
      );

      // Add pulsing ring if carrying flag
      if (carriedFlagTeam !== null) {
        const ringColor = carriedFlagTeam === 0 ? "#61afef" : "#e06c75";
        elements.push(
          <circle
            key={`${localPlayerId}-flag-ring`}
            cx={MINIMAP_SIZE / 2}
            cy={MINIMAP_SIZE / 2}
            r={10}
            fill="none"
            stroke={ringColor}
            strokeWidth={2}
            opacity={0.8}
            className="pulse-ring"
          />,
        );
      }
    }

    return <g>{elements}</g>;
  },
);

MinimapPlayers.displayName = "MinimapPlayers";

// ============================================================================
// MINIMAP RENDER COMPONENTS
// ============================================================================

/**
 * Toggle button for mobile minimap display (tap-and-hold)
 */
const MinimapToggleButton = memo(
  ({ onShow, onHide }: { onShow: () => void; onHide: () => void }) => {
    const [isHolding, setIsHolding] = useState(false);

    const handlePointerDown = (e: React.PointerEvent) => {
      e.preventDefault();
      setIsHolding(true);
      onShow();
    };

    const handlePointerUp = () => {
      setIsHolding(false);
      onHide();
    };

    return (
      <button
        type="button"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: "fixed",
          top: "60px",
          right: "20px",
          width: "48px",
          height: "48px",
          backgroundColor: isHolding
            ? "rgba(0, 0, 0, 0.9)"
            : "rgba(0, 0, 0, 0.7)",
          border: "0.5px solid rgba(255, 255, 255, 0.4)",
          borderRadius: "4px",
          color: "#ffffff",
          fontSize: "20px",
          fontWeight: "bold",
          cursor: "pointer",
          zIndex: 150,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "monospace",
          padding: 0,
          touchAction: "none",
          transition: "background-color 0.1s",
        }}
        aria-label="Hold to show minimap"
      >
        {/* Nerd Font icon: \uf279 (fa-map) from JetBrainsMono Nerd Font */}
        {"\uf279"}
      </button>
    );
  },
);

MinimapToggleButton.displayName = "MinimapToggleButton";

/**
 * SVG minimap content (shared between expanded and fixed views)
 */
const MinimapContent = memo(
  ({
    asteroids,
    pellets,
    bullets,
    players,
    flags,
    mobaBases,
    mobaNpcs,
    gameMode,
    localPlayerPos,
    localPlayerId,
    scale,
    size,
  }: {
    asteroids: Map<string, Asteroid>;
    pellets: Map<string, Pellet>;
    bullets: Map<string, Bullet>;
    players: Map<string, Player>;
    flags: Map<string, Flag>;
    mobaBases: Map<string, MOBABase>;
    mobaNpcs: Map<string, MOBANPC>;
    gameMode: string | null;
    localPlayerPos: { x: number; y: number };
    localPlayerId: string;
    scale: number;
    size: number;
  }) => {
    return (
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${size} ${size}`}
        style={{
          display: "block",
        }}
        role="img"
        aria-label="Game minimap showing player positions and entities"
      >
        <title>Game minimap showing player positions and entities</title>
        <MinimapAsteroids
          asteroids={asteroids}
          localPlayerPos={localPlayerPos}
          scale={scale}
        />
        <MinimapPellets
          pellets={pellets}
          localPlayerPos={localPlayerPos}
          scale={scale}
        />
        <MinimapBullets
          bullets={bullets}
          players={players}
          localPlayerPos={localPlayerPos}
          localPlayerId={localPlayerId}
          scale={scale}
        />
        <MinimapBases
          gameMode={gameMode}
          localPlayerPos={localPlayerPos}
          scale={scale}
        />
        <MinimapFlags
          flags={flags}
          localPlayerPos={localPlayerPos}
          scale={scale}
        />
        {gameMode === "moba" && (
          <>
            <MinimapMOBABases
              mobaBases={mobaBases}
              localPlayerPos={localPlayerPos}
              scale={scale}
            />
            <MinimapMOBANPCs
              mobaNpcs={mobaNpcs}
              localPlayerPos={localPlayerPos}
              scale={scale}
            />
          </>
        )}
        <MinimapPlayers
          players={players}
          localPlayerId={localPlayerId}
          localPlayerPos={localPlayerPos}
          scale={scale}
          flags={Array.from(flags.values())}
        />
      </svg>
    );
  },
);

MinimapContent.displayName = "MinimapContent";

/**
 * Expanded mobile minimap with backdrop
 */
const MinimapExpanded = memo(
  ({
    asteroids,
    pellets,
    bullets,
    players,
    flags,
    mobaBases,
    mobaNpcs,
    gameMode,
    localPlayerPos,
    localPlayerId,
    scale,
    onClose,
  }: {
    asteroids: Map<string, Asteroid>;
    pellets: Map<string, Pellet>;
    bullets: Map<string, Bullet>;
    players: Map<string, Player>;
    flags: Map<string, Flag>;
    mobaBases: Map<string, MOBABase>;
    mobaNpcs: Map<string, MOBANPC>;
    gameMode: string | null;
    localPlayerPos: { x: number; y: number };
    localPlayerId: string;
    scale: number;
    onClose: () => void;
  }) => {
    const backdropRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      backdropRef.current?.focus();
    }, []);

    return (
      // biome-ignore lint/a11y/useSemanticElements: Modal backdrop uses div with role="button" pattern
      <div
        ref={backdropRef}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
            onClose();
          }
        }}
        role="button"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
        tabIndex={0}
        aria-label="Close minimap overlay"
      >
        {/* biome-ignore lint/a11y/noStaticElementInteractions: Click handler only stops propagation to prevent closing when clicking map */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: Backdrop handles all keyboard interaction */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "min(80vw, 500px)",
            height: "min(80vw, 500px)",
            backgroundColor: "rgba(0, 0, 0, 0.34)",
            border: "0.5px solid rgba(255, 255, 255, 0.4)",
          }}
        >
          <MinimapContent
            asteroids={asteroids}
            pellets={pellets}
            bullets={bullets}
            players={players}
            flags={flags}
            mobaBases={mobaBases}
            mobaNpcs={mobaNpcs}
            gameMode={gameMode}
            localPlayerPos={localPlayerPos}
            localPlayerId={localPlayerId}
            scale={scale}
            size={MINIMAP_SIZE}
          />
        </div>
      </div>
    );
  },
);

MinimapExpanded.displayName = "MinimapExpanded";

/**
 * Fixed desktop minimap (bottom-right corner)
 */
const MinimapFixed = memo(
  ({
    asteroids,
    pellets,
    bullets,
    players,
    flags,
    mobaBases,
    mobaNpcs,
    gameMode,
    localPlayerPos,
    localPlayerId,
    scale,
  }: {
    asteroids: Map<string, Asteroid>;
    pellets: Map<string, Pellet>;
    bullets: Map<string, Bullet>;
    players: Map<string, Player>;
    flags: Map<string, Flag>;
    mobaBases: Map<string, MOBABase>;
    mobaNpcs: Map<string, MOBANPC>;
    gameMode: string | null;
    localPlayerPos: { x: number; y: number };
    localPlayerId: string;
    scale: number;
  }) => {
    return (
      <div
        id="minimap"
        style={{
          position: "fixed",
          bottom: `${MINIMAP_MARGIN}px`,
          right: `${MINIMAP_MARGIN}px`,
          width: `${MINIMAP_SIZE}px`,
          height: `${MINIMAP_SIZE}px`,
          backgroundColor: "rgba(0, 0, 0, 0.34)",
          border: "0.5px solid rgba(255, 255, 255, 0.4)",
          zIndex: 100,
          pointerEvents: "none",
        }}
      >
        <svg
          width={MINIMAP_SIZE}
          height={MINIMAP_SIZE}
          style={{
            display: "block",
          }}
          role="img"
          aria-label="Game minimap showing player positions and entities"
        >
          <title>Game minimap showing player positions and entities</title>
          <MinimapAsteroids
            asteroids={asteroids}
            localPlayerPos={localPlayerPos}
            scale={scale}
          />
          <MinimapPellets
            pellets={pellets}
            localPlayerPos={localPlayerPos}
            scale={scale}
          />
          <MinimapBullets
            bullets={bullets}
            players={players}
            localPlayerPos={localPlayerPos}
            localPlayerId={localPlayerId}
            scale={scale}
          />
          <MinimapBases
            gameMode={gameMode}
            localPlayerPos={localPlayerPos}
            scale={scale}
          />
          <MinimapFlags
            flags={flags}
            localPlayerPos={localPlayerPos}
            scale={scale}
          />
          {gameMode === "moba" && (
            <>
              <MinimapMOBABases
                mobaBases={mobaBases}
                localPlayerPos={localPlayerPos}
                scale={scale}
              />
              <MinimapMOBANPCs
                mobaNpcs={mobaNpcs}
                localPlayerPos={localPlayerPos}
                scale={scale}
              />
            </>
          )}
          <MinimapPlayers
            players={players}
            localPlayerId={localPlayerId}
            localPlayerPos={localPlayerPos}
            scale={scale}
            flags={Array.from(flags.values())}
          />
        </svg>
      </div>
    );
  },
);

MinimapFixed.displayName = "MinimapFixed";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Minimap component for spatial awareness and navigation
 * Displays game entities relative to local player position
 * Uses SVG for maintainable, React-based rendering
 *
 * Desktop: Fixed bottom-right position, always visible
 * Mobile: Toggle button to show/hide, centered when expanded
 */
export function MinimapComponent() {
  const players = useGameState((state) => state.players);
  const bullets = useGameState((state) => state.bullets);
  const asteroids = useGameState((state) => state.asteroids);
  const pellets = useGameState((state) => state.pellets);
  const flags = useGameState((state) => state.flags);
  const mobaBases = useGameState((state) => state.mobaBases);
  const mobaNpcs = useGameState((state) => state.mobaNpcs);
  const playerId = useGameState((state) => state.playerId);
  const gameStarted = useGameState((state) => state.gameStarted);
  const gameModeState = useGameState((state) => state.gameModeState);

  const [isMobile, setIsMobile] = useState(detectMobile());
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = detectMobile();
      setIsMobile(mobile);
      if (!mobile && isExpanded) {
        setIsExpanded(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isExpanded]);

  if (!gameStarted || !playerId) {
    return null;
  }

  const localPlayer = players.get(playerId);
  if (!localPlayer?.pos) {
    return null;
  }

  const localPlayerPos = localPlayer.pos;
  const scale = MINIMAP_SIZE / Math.max(WORLD.w, WORLD.h);
  const gameMode = gameModeState?.mode ?? null;

  const commonProps = {
    asteroids,
    pellets,
    bullets,
    players,
    flags,
    mobaBases: mobaBases ?? new Map(),
    mobaNpcs: mobaNpcs ?? new Map(),
    gameMode,
    localPlayerPos,
    localPlayerId: playerId,
    scale,
  };

  return (
    <>
      {isMobile && (
        <MinimapToggleButton
          onShow={() => setIsExpanded(true)}
          onHide={() => setIsExpanded(false)}
        />
      )}
      {isMobile && isExpanded && (
        <MinimapExpanded
          {...commonProps}
          onClose={() => setIsExpanded(false)}
        />
      )}
      {!isMobile && <MinimapFixed {...commonProps} />}
    </>
  );
}
